import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Zap, MapPin, Fuel, ArrowRight, TrendingUp, TrendingDown, Minus, List, LayoutGrid, GitCompareArrows, Check } from 'lucide-react';
import { formatPrice, formatNumber } from '@/lib/formatters';
import { powertrainLabels, drivetrainLabels, getPowertrainBadgeVariant } from '@/types/database';
import { useCompare } from '@/contexts/CompareContext';
import type { Tables } from '@/integrations/supabase/types';

type Variant = Tables<'variants'>;
type Model = Tables<'models'>;
type Brand = Tables<'brands'>;
type PowertrainSpecs = Tables<'powertrain_specs'> & Record<string, any>;
type PriceDK = Tables<'prices_dk'>;

interface ModelVariantData {
  variant: Variant;
  specs: PowertrainSpecs | null;
  lowestPrice: number | null;
  lowestPriceRecord: PriceDK | null;
  isCurrent?: boolean;
}

interface ModelVariantsProps {
  currentVariantId: string;
  model: Model;
  brand: Brand;
  currentSpecs: PowertrainSpecs | null;
  currentPrice: number | null;
  currentVariant: Variant;
}

// Helper to show difference with trend indicator
function DiffBadge({ 
  current, 
  other, 
  higherIsBetter = true,
}: { 
  current: number | null | undefined; 
  other: number | null | undefined; 
  higherIsBetter?: boolean;
}) {
  if (!current || !other || current === other) return null;
  
  const diff = other - current;
  const percentDiff = Math.round((diff / current) * 100);
  
  if (Math.abs(percentDiff) < 2) return null;
  
  const isPositive = higherIsBetter ? diff > 0 : diff < 0;
  
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] ml-1 ${isPositive ? 'text-green-600' : 'text-amber-600'}`}>
      {diff > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
      {diff > 0 ? '+' : ''}{percentDiff}%
    </span>
  );
}

export function ModelVariants({ currentVariantId, model, brand, currentSpecs, currentPrice, currentVariant }: ModelVariantsProps) {
  const [viewMode, setViewMode] = useState<'list' | 'table'>('table');
  const [activeYearByGroup, setActiveYearByGroup] = useState<Record<string, number | 'none'>>({});
  const { addCar, removeCar, isSelected, selectedCars, maxCars } = useCompare();

  const toggleCompare = (e: ReactMouseEvent, item: ModelVariantData) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelected(item.variant.id)) {
      removeCar(item.variant.id);
    } else if (selectedCars.length < maxCars) {
      addCar({
        variant: item.variant,
        model,
        brand,
        lowestPrice: item.lowestPriceRecord,
        specs: item.specs,
      });
    }
  };

  const { data: otherVariants, isLoading } = useQuery({
    queryKey: ['model-variants', model.id, currentVariantId],
    queryFn: async (): Promise<ModelVariantData[]> => {
      const { data: variants, error } = await supabase
        .from('variants')
        .select(`
          *,
          powertrain_specs (*),
          prices_dk (*)
        `)
        .eq('model_id', model.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      if (!variants) return [];

      return variants.map((v) => {
        const specs = Array.isArray(v.powertrain_specs) ? v.powertrain_specs[0] : v.powertrain_specs;
        const prices = v.prices_dk as PriceDK[];
        const lowestPriceRecord = prices.length > 0
          ? prices.reduce((min, p) => (p.price_dkk < min.price_dkk ? p : min), prices[0])
          : null;
        const lowestPrice = lowestPriceRecord
          ? lowestPriceRecord.price_dkk
          : ((v as Variant).expected_price_dkk ?? null);

        return {
          variant: v as Variant,
          specs: specs || null,
          lowestPrice,
          lowestPriceRecord,
          isCurrent: v.id === currentVariantId,
        };
      });
    },
    enabled: !!model.id,
  });

  if (isLoading || !otherVariants || otherVariants.length <= 1) {
    return null;
  }

  // Billigste først — varianter uden pris til sidst
  const priceOf = (item: ModelVariantData) => item.lowestPrice ?? Infinity;
  const byPrice = (a: ModelVariantData, b: ModelVariantData) => priceOf(a) - priceOf(b);

  // Group variants by trim_level
  const groupedVariants = otherVariants.reduce((acc, item) => {
    const trimLevel = item.variant.trim_level || 'Øvrige varianter';
    if (!acc[trimLevel]) {
      acc[trimLevel] = [];
    }
    acc[trimLevel].push(item);
    return acc;
  }, {} as Record<string, ModelVariantData[]>);

  Object.values(groupedVariants).forEach((list) => list.sort(byPrice));

  // Sort trim levels by their cheapest variant (billigste gruppe først)
  const currentTrimLevel = currentVariant.trim_level || 'Øvrige varianter';
  const cheapestOfTrim = (trim: string) =>
    Math.min(...groupedVariants[trim].map(priceOf));
  const sortedTrimLevels = Object.keys(groupedVariants).sort((a, b) => {
    const d = cheapestOfTrim(a) - cheapestOfTrim(b);
    return d !== 0 ? d : a.localeCompare(b, 'da');
  });


  const hasTrimLevels = sortedTrimLevels.length > 1 || sortedTrimLevels[0] !== 'Øvrige varianter';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Alle {brand.name} {model.name} varianter
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs font-normal">
              {otherVariants.length} varianter
            </Badge>
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2 rounded-r-none"
                onClick={() => setViewMode('table')}
                aria-label="Vis varianter som tabel"
                aria-pressed={viewMode === 'table'}
                title="Tabelvisning"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2 rounded-l-none"
                onClick={() => setViewMode('list')}
                aria-label="Vis varianter som liste"
                aria-pressed={viewMode === 'list'}
                title="Listevisning"
              >
                <List className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {sortedTrimLevels.map((trimLevel) => {
          const variants = groupedVariants[trimLevel];

          // Sub-group by variant name to collapse model-year siblings into one row
          type YearGroup = { key: string; name: string; items: ModelVariantData[] };
          const yearGroupsMap = new Map<string, YearGroup>();
          for (const it of variants) {
            const key = `${trimLevel}::${it.variant.name.toLowerCase()}`;
            if (!yearGroupsMap.has(key)) {
              yearGroupsMap.set(key, { key, name: it.variant.name, items: [] });
            }
            yearGroupsMap.get(key)!.items.push(it);
          }
          const yearGroups = Array.from(yearGroupsMap.values())
            .map((g) => {
              // newest first; null years last
              g.items.sort((a, b) => {
                const ay = a.variant.model_year ?? -Infinity;
                const by = b.variant.model_year ?? -Infinity;
                return by - ay;
              });
              return g;
            })
            // billigste variantgruppe øverst (grupper uden pris til sidst)
            .sort((a, b) => {
              const pa = Math.min(...a.items.map(priceOf));
              const pb = Math.min(...b.items.map(priceOf));
              const d = pa - pb;
              return d !== 0 ? d : a.name.localeCompare(b.name, 'da');
            });


          const pickActiveItem = (g: YearGroup): ModelVariantData => {
            const stored = activeYearByGroup[g.key];
            if (stored !== undefined) {
              const match = g.items.find((it) =>
                stored === 'none' ? it.variant.model_year == null : it.variant.model_year === stored,
              );
              if (match) return match;
            }
            // default: current variant if part of group, else newest
            const cur = g.items.find((it) => it.isCurrent);
            return cur ?? g.items[0];
          };

          const isCurrentTrimGroup = trimLevel === currentTrimLevel;

          return (
            <div key={trimLevel}>
              {hasTrimLevels && (
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="font-semibold text-sm">{trimLevel}</h4>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {variants.length}
                  </Badge>
                  {isCurrentTrimGroup && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Dit udstyrsniveau
                    </Badge>
                  )}
                </div>
              )}
              
              {viewMode === 'table' ? (
                <div className="overflow-x-auto -mx-6">
                  <Table>
                    {!hasTrimLevels && (
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">Variant</TableHead>
                          <TableHead className="text-right">Pris</TableHead>
                          <TableHead className="text-right">Ydelse</TableHead>
                          <TableHead className="text-right">Rækkevidde</TableHead>
                          <TableHead className="text-right">Træk</TableHead>
                          <TableHead className="pr-6"></TableHead>
                        </TableRow>
                      </TableHeader>
                    )}
                    <TableBody>
                      {yearGroups.map((g) => {
                        const item = pickActiveItem(g);
                        const hasYears = g.items.length > 1;
                        const isEV = item.variant.powertrain_type === 'EV';
                        const isHybrid = (item.variant.powertrain_type as string) === 'PHEV' || (item.variant.powertrain_type as string) === 'Hybrid';
                        
                        return (
                          <TableRow 
                            key={g.key} 
                            className={item.isCurrent ? 'bg-primary/5' : 'hover:bg-muted/50'}
                          >
                            <TableCell className="pl-6">
                              <div className="flex items-center gap-2 flex-wrap">
                                {item.isCurrent ? (
                                  <span className="font-medium">{item.variant.name}</span>
                                ) : (
                                  <Link 
                                    to={`/biler/${brand.slug}/${model.slug}/${item.variant.slug}`}
                                    className="font-medium hover:text-primary transition-colors"
                                  >
                                    {item.variant.name}
                                  </Link>
                                )}
                                <Badge 
                                  variant={getPowertrainBadgeVariant(item.variant.powertrain_type)}
                                  className="text-[10px] px-1.5 py-0"
                                >
                                  {powertrainLabels[item.variant.powertrain_type]}
                                </Badge>
                                {item.isCurrent && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    Denne
                                  </Badge>
                                )}
                                {hasYears && (
                                  <div className="inline-flex items-center gap-1 ml-1">
                                    {g.items.map((yi) => {
                                      const y = yi.variant.model_year;
                                      const label = y != null ? String(y) : '—';
                                      const active = yi.variant.id === item.variant.id;
                                      return (
                                        <button
                                          key={yi.variant.id}
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setActiveYearByGroup((prev) => ({ ...prev, [g.key]: y ?? 'none' }));
                                          }}
                                          className={`text-[10px] px-1.5 py-0.5 rounded border transition ${active ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                                          title={`Modelår ${label}`}
                                        >
                                          {label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-semibold text-primary">
                                {item.lowestPrice ? formatPrice(item.lowestPrice) : '-'}
                              </span>
                              {!item.isCurrent && (
                                <DiffBadge 
                                  current={currentPrice} 
                                  other={item.lowestPrice} 
                                  higherIsBetter={false}
                                />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span>{item.specs?.power_hp ? `${item.specs.power_hp} hk` : '-'}</span>
                              {!item.isCurrent && (
                                <DiffBadge 
                                  current={currentSpecs?.power_hp} 
                                  other={item.specs?.power_hp} 
                                  higherIsBetter={true}
                                />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {(isEV || isHybrid) ? (
                                <>
                                  <span>{item.specs?.range_wltp_km ? `${formatNumber(item.specs.range_wltp_km)} km` : '-'}</span>
                                  {!item.isCurrent && (
                                    <DiffBadge 
                                      current={currentSpecs?.range_wltp_km} 
                                      other={item.specs?.range_wltp_km} 
                                      higherIsBetter={true}
                                    />
                                  )}
                                </>
                              ) : (
                                <>
                                  <span>{item.specs?.l_per_100km ? `${item.specs.l_per_100km} L/100km` : '-'}</span>
                                  {!item.isCurrent && (
                                    <DiffBadge 
                                      current={currentSpecs?.l_per_100km ? Number(currentSpecs.l_per_100km) : null} 
                                      other={item.specs?.l_per_100km ? Number(item.specs.l_per_100km) : null} 
                                      higherIsBetter={false}
                                    />
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground text-sm">
                              {drivetrainLabels[item.variant.drivetrain]}
                            </TableCell>
                            <TableCell className="pr-6 text-right">
                              {!item.isCurrent && (
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant={isSelected(item.variant.id) ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-8 px-2 text-xs"
                                    onClick={(e) => toggleCompare(e, item)}
                                    disabled={!isSelected(item.variant.id) && selectedCars.length >= maxCars}
                                  >
                                    {isSelected(item.variant.id) ? (
                                      <><Check className="w-3 h-3 mr-1" />Valgt</>
                                    ) : (
                                      <><GitCompareArrows className="w-3 h-3 mr-1" />Sammenlign</>
                                    )}
                                  </Button>
                                  <Link
                                    to={`/biler/${brand.slug}/${model.slug}/${item.variant.slug}`}
                                    className="inline-flex items-center text-xs text-primary hover:underline"
                                  >
                                    Se <ArrowRight className="w-3 h-3 ml-1" />
                                  </Link>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="space-y-3">
                  {variants.filter(v => !v.isCurrent).map((item) => {
                    const isEV = item.variant.powertrain_type === 'EV';
                    const isHybrid = (item.variant.powertrain_type as string) === 'PHEV' || (item.variant.powertrain_type as string) === 'Hybrid';
                    
                    return (
                      <Link
                        key={item.variant.id}
                        to={`/biler/${brand.slug}/${model.slug}/${item.variant.slug}`}
                        className="block p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/5 transition-all group"
                      >
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="font-medium truncate group-hover:text-primary transition-colors">
                              {item.variant.name}
                            </p>
                            <Badge 
                              variant={getPowertrainBadgeVariant(item.variant.powertrain_type)}
                              className="text-[10px] px-1.5 py-0 flex-shrink-0"
                            >
                              {powertrainLabels[item.variant.powertrain_type]}
                            </Badge>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                        </div>
                        
                        {/* Comparison grid */}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {/* Price comparison */}
                          <div className="bg-muted/50 rounded p-2">
                            <p className="text-muted-foreground mb-0.5">Pris</p>
                            <p className="font-semibold text-primary">
                              {item.lowestPrice ? formatPrice(item.lowestPrice) : '-'}
                            </p>
                            <DiffBadge 
                              current={currentPrice} 
                              other={item.lowestPrice} 
                              higherIsBetter={false}
                            />
                          </div>
                          
                          {/* Power comparison */}
                          <div className="bg-muted/50 rounded p-2">
                            <p className="text-muted-foreground mb-0.5 flex items-center gap-1">
                              <Zap className="w-3 h-3" /> Ydelse
                            </p>
                            <p className="font-semibold">
                              {item.specs?.power_hp ? `${item.specs.power_hp} hk` : '-'}
                            </p>
                            <DiffBadge 
                              current={currentSpecs?.power_hp} 
                              other={item.specs?.power_hp} 
                              higherIsBetter={true}
                            />
                          </div>
                          
                          {/* Range or fuel consumption */}
                          <div className="bg-muted/50 rounded p-2">
                            {(isEV || isHybrid) ? (
                              <>
                                <p className="text-muted-foreground mb-0.5 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> Rækkevidde
                                </p>
                                <p className="font-semibold">
                                  {item.specs?.range_wltp_km ? `${formatNumber(item.specs.range_wltp_km)} km` : '-'}
                                </p>
                                <DiffBadge 
                                  current={currentSpecs?.range_wltp_km} 
                                  other={item.specs?.range_wltp_km} 
                                  higherIsBetter={true}
                                />
                              </>
                            ) : (
                              <>
                                <p className="text-muted-foreground mb-0.5 flex items-center gap-1">
                                  <Fuel className="w-3 h-3" /> Forbrug
                                </p>
                                <p className="font-semibold">
                                  {item.specs?.l_per_100km ? `${item.specs.l_per_100km} L` : '-'}
                                </p>
                                <DiffBadge 
                                  current={currentSpecs?.l_per_100km ? Number(currentSpecs.l_per_100km) : null} 
                                  other={item.specs?.l_per_100km ? Number(item.specs.l_per_100km) : null} 
                                  higherIsBetter={false}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Drivetrain + compare */}
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <p className="text-[10px] text-muted-foreground">
                            {drivetrainLabels[item.variant.drivetrain]}
                          </p>
                          <Button
                            type="button"
                            variant={isSelected(item.variant.id) ? 'default' : 'outline'}
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={(e) => toggleCompare(e, item)}
                            disabled={!isSelected(item.variant.id) && selectedCars.length >= maxCars}
                          >
                            {isSelected(item.variant.id) ? (
                              <><Check className="w-3 h-3 mr-1" />Valgt</>
                            ) : (
                              <><GitCompareArrows className="w-3 h-3 mr-1" />Sammenlign</>
                            )}
                          </Button>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
